import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AppearanceColorKey, IAppearanceColor } from '@web/dtos';
import { SectionComponent } from '../layout/section/section.component';
import { SectionHeaderComponent } from '../layout/section/section-header/section-header.component';
import { CmsTextProviderService } from '../../services/cms-text-provider.service';
import { ICorporateDesignForm } from '../../entities/coporate-design-entity';
import { ColorPickerComponent } from '../data-input/color-picker/color-picker.component';
import { ColorPickerListComponent } from '../data-input/color-picker-list/color-picker-list';

@Component({
  selector: 'aw-appearance-standard-controls',
  imports: [
    NgClass,
    NgTemplateOutlet,
    ColorPickerComponent,
    SectionComponent,
    SectionHeaderComponent,
    ColorPickerListComponent
  ],
  templateUrl: './appearance-standard-controls.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppearanceStandardControlsComponent {
  public readonly forceCollapse: InputSignal<boolean> = input(false);
  public readonly designForm: InputSignal<FormGroup<ICorporateDesignForm>> =
    input.required<FormGroup<ICorporateDesignForm>>();

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  protected readonly colors: IAppearanceColor[] = [
    {
      key: 'fill',
      label: this.cmsTextProvider.appearanceText.controlsFillColour,
      description: this.cmsTextProvider.appearanceText.controlsFillColourHint,
    },
    {
      key: 'contrast',
      label: this.cmsTextProvider.appearanceText.controlsContrast,
    },
    {
      key: 'border',
      label: this.cmsTextProvider.appearanceText.controlsBorder,
      description: this.cmsTextProvider.appearanceText.controlsBorderHint,
    },
    {
      key: 'outline',
      label: this.cmsTextProvider.appearanceText.controlsOutline,
      description: this.cmsTextProvider.appearanceText.controlsOutlineHint,
    },
  ];

  protected fillColor(isDark: boolean): string {
    return this.colorControl('fill', isDark).value;
  }

  protected contrastColor(isDark: boolean): string {
    return this.colorControl('contrast', isDark).value;
  }

  protected borderColor(isDark: boolean): string {
    return this.colorControl('border', isDark).value;
  }

  protected outlineColor(isDark: boolean): string {
    return this.colorControl('outline', isDark).value;
  }

  protected swatchColor(key: AppearanceColorKey, isDark: boolean): string {
    switch (key) {
      case 'fill':
        return this.fillColor(isDark);
      case 'contrast':
        return this.contrastColor(isDark);
      case 'border':
        return this.borderColor(isDark);
      case 'outline':
        return this.outlineColor(isDark);
    }
  }

  protected colorControl(
    key: AppearanceColorKey,
    isDark: boolean
  ): FormControl<string> {
    switch (key) {
      case 'fill':
        return isDark
          ? this.designForm().controls.primaryDarkColor
          : this.designForm().controls.primaryColor;
      case 'contrast':
        return isDark
          ? this.designForm().controls.primaryDarkFontColor
          : this.designForm().controls.primaryFontColor;
      case 'border':
        return isDark
          ? this.designForm().controls.primaryDarkBorderColor
          : this.designForm().controls.primaryBorderColor;
      case 'outline':
        return isDark
          ? this.designForm().controls.primaryDarkAlternativeColor
          : this.designForm().controls.primaryAlternativeColor;
    }
  }

  protected colorPickerId(key: AppearanceColorKey, isDark: boolean): string {
    return `controls-${isDark ? 'dark' : 'light'}-${key}`;
  }

  protected selectColor(
    key: AppearanceColorKey,
    isDark: boolean,
    color: string
  ): void {
    this.colorControl(key, isDark).setValue(`#${color.replace(/^#/, '')}`);
  }
}
